import type { DiGraph } from "./digraph.js";

/** All `u` with a directed u→…→`v` path of positive-weight edges. */
export function ancestors(g: DiGraph, v: string): Set<string> {
  const out = new Set<string>();
  const q: string[] = [v];
  const seen = new Set<string>([v]);
  while (q.length) {
    const x = q.shift()!;
    for (const { u, w: wt } of g.inNeighbors(x)) {
      if (wt <= 0) continue;
      if (seen.has(u)) continue;
      seen.add(u);
      out.add(u);
      q.push(u);
    }
  }
  return out;
}

/** All `w` with a directed v→…→`w` path. */
export function descendants(g: DiGraph, v: string): Set<string> {
  const out = new Set<string>();
  const q: string[] = [v];
  const seen = new Set<string>([v]);
  while (q.length) {
    const x = q.shift()!;
    for (const { v: t, w: wt } of g.outNeighbors(x)) {
      if (wt <= 0) continue;
      if (seen.has(t)) continue;
      seen.add(t);
      out.add(t);
      q.push(t);
    }
  }
  return out;
}

/**
 * Unweighted undirected hop distance between two nodes, or Infinity if disconnected.
 * Uses the underlying undirected simple graph; parallel directed edges coalesce to one.
 */
export function undirectedHops(g: DiGraph, a: string, b: string): number {
  if (a === b) return 0;
  const und = g.toUndirectedEdgeSet();
  const adj = new Map<string, Set<string>>();
  for (const { u, v, w: wt } of und) {
    if (wt <= 0) continue;
    if (!adj.has(u)) adj.set(u, new Set());
    if (!adj.has(v)) adj.set(v, new Set());
    adj.get(u)!.add(v);
    adj.get(v)!.add(u);
  }
  for (const x of g.nodes) {
    if (!adj.has(x)) adj.set(x, new Set());
  }
  if (!g.hasNode(a) || !g.hasNode(b)) return Number.POSITIVE_INFINITY;
  const d = new Map<string, number>();
  const q: string[] = [a];
  d.set(a, 0);
  for (let i = 0; i < q.length; i++) {
    const u = q[i]!;
    if (u === b) return d.get(u)!;
    for (const v of adj.get(u) ?? []) {
      if (d.has(v)) continue;
      d.set(v, d.get(u)! + 1);
      q.push(v);
    }
  }
  return Number.POSITIVE_INFINITY;
}
